import React, { useEffect, useState, useCallback } from 'react';
import { serviceCatalogApi } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Brain,
  Eye,
  FileText,
  Mic,
  Image,
  Heart,
  Search,
  MessageSquare,
  Sparkles,
  Layers,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  Pencil,
  Save,
  X,
  Play,
  Check,
} from 'lucide-react';

const CATEGORY_META = {
  classify: { label: 'Classifiers', description: 'LLM intelligence that categorizes and scores content', color: 'bg-violet-500/10 text-violet-600', icon: Brain },
  extract: { label: 'Extractors', description: 'Modality conversion — transform content into text', color: 'bg-blue-500/10 text-blue-600', icon: Eye },
  enrich: { label: 'Enrichers', description: 'Graph-aware post-classification processing', color: 'bg-amber-500/10 text-amber-600', icon: Sparkles },
  system: { label: 'System', description: 'Platform infrastructure services', color: 'bg-emerald-500/10 text-emerald-600', icon: Layers },
};

const SERVICE_ICONS = {
  'classify:root-primitives': Brain,
  'classify:tension-axes': Sparkles,
  'classify:concept-classification': FileText,
  'classify:people-classification': MessageSquare,
  'classify:org-domains': Layers,
  'extract:image-analyzer': Image,
  'extract:text-parser': FileText,
  'extract:audio-transcriber': Mic,
  'enrich:contextualize': Search,
  'system:plan-query': Search,
  'system:synthesize-answer': MessageSquare,
  'system:heartbeat': Heart,
  'system:generate-schema': Sparkles,
};

// Model registry — mirrors service-catalog.js MODEL_REGISTRY
const MODEL_REGISTRY = {
  'claude-haiku-4-5-20251001': {
    provider: 'anthropic', name: 'Haiku 4.5',
    capabilities: ['json', 'vision', 'tool_use'],
    context_window: 200000,
    cost: { input: 0.80, output: 4.00 },
  },
  'claude-sonnet-4-6-20250514': {
    provider: 'anthropic', name: 'Sonnet 4.6',
    capabilities: ['json', 'vision', 'tool_use'],
    context_window: 200000,
    cost: { input: 3.00, output: 15.00 },
  },
  'claude-opus-4-6-20250514': {
    provider: 'anthropic', name: 'Opus 4.6',
    capabilities: ['json', 'vision', 'tool_use'],
    context_window: 200000,
    cost: { input: 15.00, output: 75.00 },
  },
  'gemini-2.0-flash': {
    provider: 'google', name: 'Gemini 2.0 Flash',
    capabilities: ['json', 'vision'],
    context_window: 1000000,
    cost: { input: 0.10, output: 0.40 },
  },
  'us.deepseek.deepseek-r1-0528-v1:0': {
    provider: 'bedrock', name: 'DeepSeek R1 (Bedrock)',
    capabilities: ['json'],
    context_window: 128000,
    cost: { input: 1.35, output: 5.40 },
  },
  'us.amazon.nova-lite-v1:0': {
    provider: 'bedrock', name: 'Nova Lite (Bedrock)',
    capabilities: ['json', 'vision'],
    context_window: 300000,
    cost: { input: 0.06, output: 0.24 },
  },
  'us.amazon.nova-micro-v1:0': {
    provider: 'bedrock', name: 'Nova Micro (Bedrock)',
    capabilities: ['json'],
    context_window: 128000,
    cost: { input: 0.035, output: 0.14 },
  },
};

const PROVIDERS = [
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'google', label: 'Google' },
  { id: 'bedrock', label: 'AWS Bedrock' },
];

function getModelsForProvider(provider) {
  return Object.entries(MODEL_REGISTRY)
    .filter(([, m]) => m.provider === provider)
    .map(([id, m]) => ({ id, ...m }));
}

function estimateCost(service) {
  const model = service?.llm_config?.model;
  const reg = MODEL_REGISTRY[model];
  const est = service?.processing?.estimated_tokens;
  if (!reg || !est) return null;
  const inputCost = (est.input / 1_000_000) * reg.cost.input;
  const outputCost = (est.output / 1_000_000) * reg.cost.output;
  return (inputCost + outputCost);
}

const TEMP_PRESETS = [
  { label: 'Deterministic', value: 0.0 },
  { label: 'Balanced', value: 0.3 },
  { label: 'Creative', value: 0.7 },
  { label: 'Max', value: 1.0 },
];

// Guidelines toggle definitions — mirrors service-catalog.js guidelinesFromToggles()
const TOGGLE_DEFS = [
  {
    key: 'graph_terminology',
    label: 'Graph Terminology',
    help: 'How internal graph terms (node types, edge types) appear in output',
    options: [
      { value: 'hidden', label: 'Hidden', desc: 'Never expose graph terms — use Data Dictionary translations' },
      { value: 'translated', label: 'Translated', desc: 'Use human-readable translations from Data Dictionary' },
      { value: 'raw', label: 'Raw', desc: 'Graph terms shown as-is (admin/developer only)' },
    ],
  },
  {
    key: 'audience',
    label: 'Audience',
    help: 'Who sees this service\'s output',
    options: [
      { value: 'end_user', label: 'End User', desc: 'No mention of services, pipelines, or infrastructure' },
      { value: 'developer', label: 'Developer', desc: 'Can reference service names and edge types' },
      { value: 'admin', label: 'Admin', desc: 'Full system visibility' },
    ],
  },
  {
    key: 'grounding',
    label: 'Grounding',
    help: 'Whether the LLM must reference the user\'s actual content',
    options: [
      { value: 'must_reference_content', label: 'Must Reference', desc: 'Always reference specific user content' },
      { value: 'optional', label: 'Optional', desc: 'Reference when available, general OK' },
      { value: 'none', label: 'None', desc: 'No grounding requirement' },
    ],
  },
  {
    key: 'response_length',
    label: 'Response Length',
    help: 'How verbose the LLM output should be',
    options: [
      { value: 'brief', label: 'Brief', desc: 'Under 2 sentences per item' },
      { value: 'moderate', label: 'Moderate', desc: '2-4 sentences per item' },
      { value: 'detailed', label: 'Detailed', desc: 'Thorough and detailed' },
    ],
  },
  {
    key: 'persona',
    label: 'Persona',
    help: 'The tone and style of the LLM\'s voice',
    options: [
      { value: 'collaborator', label: 'Collaborator', desc: 'Thoughtful peer following the user\'s work' },
      { value: 'analyst', label: 'Analyst', desc: 'Precise, evidence-focused' },
      { value: 'coach', label: 'Coach', desc: 'Supportive, actionable suggestions' },
    ],
  },
];

// Client-side preview of guidelines text from toggles (mirrors server-side guidelinesFromToggles)
function previewGuidelinesText(toggles = {}, custom = '') {
  const TEXT_MAP = {
    graph_terminology: {
      hidden: 'Never expose internal graph terminology (node types, edge types, service names). Use the app\'s Data Dictionary translations instead.',
      translated: 'When referencing graph concepts, use their human-readable translations from the Data Dictionary.',
    },
    audience: {
      end_user: 'Frame all output for the app\'s end user. Never reference services, pipelines, classifiers, or system infrastructure.',
      developer: 'Output is for a developer or technical user. Reference service names and edge types when relevant.',
    },
    grounding: {
      must_reference_content: 'Always reference specific content the user has previously provided. Never generate questions or statements that could apply to anyone.',
      optional: 'Reference existing content when available, but general responses are acceptable.',
    },
    response_length: {
      brief: 'Keep responses concise — under 2 sentences per item.',
      moderate: 'Responses can be moderately detailed — 2-4 sentences per item.',
      detailed: 'Be thorough and detailed in responses.',
    },
    persona: {
      collaborator: 'Adopt the tone of a thoughtful collaborator who has been following the user\'s work closely.',
      analyst: 'Be precise and analytical. Focus on accuracy and evidence.',
      coach: 'Guide the user with supportive, actionable suggestions.',
    },
  };

  const lines = [];
  for (const [key, map] of Object.entries(TEXT_MAP)) {
    const val = toggles[key];
    if (val && map[val]) lines.push(map[val]);
  }
  return lines.join('\n') + (custom ? '\n' + custom : '');
}

// ==========================================
// SERVICE CREATE FORM
// ==========================================

const CATEGORY_OPTIONS = [
  { value: 'classify', label: 'Classifier', desc: 'Categorizes and scores content' },
  { value: 'enrich', label: 'Enricher', desc: 'Graph-aware post-processing' },
  { value: 'system', label: 'System', desc: 'Platform infrastructure service' },
];

function ServiceCreateForm({ onCreated, onCancel }) {
  const [draft, setDraft] = useState({
    name: '',
    description: '',
    category: 'classify',
    provider: 'anthropic',
    model: 'claude-haiku-4-5-20251001',
    temperature: 0.3,
    max_tokens: 2048,
    role: '',
    task: '',
    guidelines_toggles: {},
    guidelines_custom: '',
    permissions: { can_write_graph: true, can_modify_existing: false, can_trigger_services: false, write_mode: 'append' },
    node_types: [],
    edge_types: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Node type editor helpers
  const addNodeType = () => setDraft(d => ({
    ...d, node_types: [...d.node_types, { label: '', properties: '' }],
  }));
  const removeNodeType = (i) => setDraft(d => ({
    ...d, node_types: d.node_types.filter((_, idx) => idx !== i),
  }));
  const updateNodeType = (i, field, val) => setDraft(d => ({
    ...d, node_types: d.node_types.map((nt, idx) => idx === i ? { ...nt, [field]: val } : nt),
  }));

  // Edge type editor helpers
  const addEdgeType = () => setDraft(d => ({
    ...d, edge_types: [...d.edge_types, { from: '', to: '', type: '' }],
  }));
  const removeEdgeType = (i) => setDraft(d => ({
    ...d, edge_types: d.edge_types.filter((_, idx) => idx !== i),
  }));
  const updateEdgeType = (i, field, val) => setDraft(d => ({
    ...d, edge_types: d.edge_types.map((et, idx) => idx === i ? { ...et, [field]: val } : et),
  }));

  const handleCreate = async () => {
    if (!draft.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        category: draft.category,
        llm_config: {
          provider: draft.provider,
          model: draft.model,
          temperature: draft.temperature,
          max_tokens: draft.max_tokens,
        },
        prompt_config: {
          role: draft.role,
          task: draft.task,
          guidelines_toggles: draft.guidelines_toggles,
          guidelines_custom: draft.guidelines_custom,
        },
        permissions: draft.permissions,
        schema_contribution: {
          node_types: draft.node_types
            .filter(nt => nt.label.trim())
            .map(nt => ({
              label: nt.label.trim(),
              properties: nt.properties ? nt.properties.split(',').map(p => p.trim()).filter(Boolean) : [],
            })),
          edge_types: draft.edge_types
            .filter(et => et.from.trim() && et.to.trim() && et.type.trim())
            .map(et => ({ from: et.from.trim(), to: et.to.trim(), type: et.type.trim() })),
        },
      };
      await serviceCatalogApi.create(payload);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const previewId = draft.name.trim()
    ? `${draft.category}:${draft.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`
    : `${draft.category}:...`;

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-sm">Create New Service</CardTitle>
        <CardDescription className="text-xs">
          Define a new AI service. It will be immediately available for subscription.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && (
          <div className="p-2 rounded-md bg-destructive/10 text-destructive text-xs">{error}</div>
        )}

        {/* Identity */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Identity</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Category</label>
              <select
                value={draft.category}
                onChange={(e) => setDraft(d => ({ ...d, category: e.target.value }))}
                className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
              >
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Name</label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="e.g., Character Arc Classifier"
                className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Service ID (auto)</label>
              <div className="text-xs px-2 py-1.5 rounded-md border bg-muted text-muted-foreground font-mono">
                {previewId}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <label className="text-xs text-muted-foreground block mb-1">Description</label>
            <input
              type="text"
              value={draft.description}
              onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="What does this service do?"
              className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
            />
          </div>
        </div>

        {/* Model Config */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Model</h4>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Provider</label>
              <select
                value={draft.provider}
                onChange={(e) => {
                  const p = e.target.value;
                  const models = getModelsForProvider(p);
                  setDraft(d => ({ ...d, provider: p, model: models[0]?.id || '' }));
                }}
                className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
              >
                {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Model</label>
              <select
                value={draft.model}
                onChange={(e) => setDraft(d => ({ ...d, model: e.target.value }))}
                className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
              >
                {getModelsForProvider(draft.provider).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Temperature: {draft.temperature}</label>
              <input type="range" min="0" max="1" step="0.1" value={draft.temperature}
                onChange={(e) => setDraft(d => ({ ...d, temperature: parseFloat(e.target.value) }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Max Tokens</label>
              <input type="number" value={draft.max_tokens}
                onChange={(e) => setDraft(d => ({ ...d, max_tokens: parseInt(e.target.value) || 2048 }))}
                className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
                min="256" max="16384" step="256"
              />
            </div>
          </div>
          {MODEL_REGISTRY[draft.model] && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-emerald-500">
                ${MODEL_REGISTRY[draft.model].cost.input}/{MODEL_REGISTRY[draft.model].cost.output} per M tokens
              </span>
              {MODEL_REGISTRY[draft.model].capabilities.map(cap => (
                <Badge key={cap} variant="outline" className="text-[10px] font-normal">{cap}</Badge>
              ))}
            </div>
          )}
        </div>

        {/* Prompts */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Role</h4>
          <textarea
            value={draft.role}
            onChange={(e) => setDraft(d => ({ ...d, role: e.target.value }))}
            placeholder="You are a..."
            className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y min-h-[60px]"
            rows={3}
          />
        </div>
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Task</h4>
          <textarea
            value={draft.task}
            onChange={(e) => setDraft(d => ({ ...d, task: e.target.value }))}
            placeholder="For the given content, extract..."
            className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y min-h-[60px]"
            rows={3}
          />
        </div>

        {/* Guidelines Toggles */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Guidelines</h4>
          <div className="space-y-2">
            {TOGGLE_DEFS.map((td) => (
              <div key={td.key} className="flex items-center gap-2">
                <label className="text-xs font-medium text-foreground w-28 flex-shrink-0">{td.label}</label>
                <div className="flex gap-1">
                  {td.options.map((opt) => (
                    <button key={opt.value} type="button"
                      onClick={() => setDraft(d => ({ ...d, guidelines_toggles: { ...d.guidelines_toggles, [td.key]: opt.value } }))}
                      className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                        draft.guidelines_toggles[td.key] === opt.value
                          ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:text-foreground'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Schema Contribution */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schema Contribution</h4>
          <div className="space-y-3">
            {/* Node Types */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted-foreground">Node Types</label>
                <button type="button" onClick={addNodeType}
                  className="text-[10px] text-primary hover:underline">+ Add Node Type</button>
              </div>
              {draft.node_types.map((nt, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input type="text" value={nt.label} placeholder="Label (e.g., CharacterArc)"
                    onChange={(e) => updateNodeType(i, 'label', e.target.value)}
                    className="flex-1 text-xs px-2 py-1 rounded border bg-background"
                  />
                  <input type="text" value={nt.properties} placeholder="Properties (comma-separated)"
                    onChange={(e) => updateNodeType(i, 'properties', e.target.value)}
                    className="flex-1 text-xs px-2 py-1 rounded border bg-background"
                  />
                  <button type="button" onClick={() => removeNodeType(i)}
                    className="text-xs text-destructive hover:underline px-1">x</button>
                </div>
              ))}
            </div>
            {/* Edge Types */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted-foreground">Edge Types</label>
                <button type="button" onClick={addEdgeType}
                  className="text-[10px] text-primary hover:underline">+ Add Edge Type</button>
              </div>
              {draft.edge_types.map((et, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <input type="text" value={et.from} placeholder="From"
                    onChange={(e) => updateEdgeType(i, 'from', e.target.value)}
                    className="w-1/4 text-xs px-2 py-1 rounded border bg-background"
                  />
                  <input type="text" value={et.type} placeholder="EDGE_TYPE"
                    onChange={(e) => updateEdgeType(i, 'type', e.target.value)}
                    className="w-1/4 text-xs px-2 py-1 rounded border bg-background"
                  />
                  <input type="text" value={et.to} placeholder="To"
                    onChange={(e) => updateEdgeType(i, 'to', e.target.value)}
                    className="w-1/4 text-xs px-2 py-1 rounded border bg-background"
                  />
                  <button type="button" onClick={() => removeEdgeType(i)}
                    className="text-xs text-destructive hover:underline px-1">x</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t pt-4">
          <button onClick={handleCreate} disabled={saving || !draft.name.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Create Service
          </button>
          <button onClick={onCancel}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-md border hover:bg-muted transition-colors"
          >
            <X className="h-3 w-3" /> Cancel
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// SERVICE EDIT FORM
// ==========================================

function ServiceEditForm({ service, onSave, onCancel }) {
  const currentModel = service.llm_config?.model || '';
  const currentReg = MODEL_REGISTRY[currentModel];
  const [draft, setDraft] = useState(() => ({
    provider: service.llm_config?.provider || currentReg?.provider || 'anthropic',
    model: currentModel,
    temperature: service.llm_config?.temperature ?? 0,
    max_tokens: service.llm_config?.max_tokens || 2048,
    role: service.prompt_config?.role || '',
    task: service.prompt_config?.task || '',
    guidelines_toggles: service.prompt_config?.guidelines_toggles || {},
    guidelines_custom: service.prompt_config?.guidelines_custom || '',
    permissions: service.permissions || {},
  }));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates = {
        llm_config: {
          ...service.llm_config,
          provider: draft.provider,
          model: draft.model,
          temperature: draft.temperature,
          max_tokens: draft.max_tokens,
        },
        prompt_config: {
          role: draft.role,
          task: draft.task,
          guidelines_toggles: draft.guidelines_toggles,
          guidelines_custom: draft.guidelines_custom,
        },
        permissions: draft.permissions,
      };
      await serviceCatalogApi.update(service.serviceId, updates);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const res = await serviceCatalogApi.test(service.serviceId, { text: testText });
      setTestResult(res.data?.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-4 space-y-5 border-t pt-4" onClick={(e) => e.stopPropagation()}>
      {error && (
        <div className="p-2 rounded-md bg-destructive/10 text-destructive text-xs">{error}</div>
      )}

      {/* Model Config */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Model Configuration</h4>
        <div className="grid grid-cols-4 gap-3">
          {/* Provider */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Provider</label>
            <select
              value={draft.provider}
              onChange={(e) => {
                const newProvider = e.target.value;
                const models = getModelsForProvider(newProvider);
                const firstModel = models[0]?.id || '';
                setDraft(d => ({ ...d, provider: newProvider, model: firstModel }));
              }}
              className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
            >
              {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          {/* Model */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Model</label>
            <select
              value={draft.model}
              onChange={(e) => setDraft(d => ({ ...d, model: e.target.value }))}
              className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
            >
              {getModelsForProvider(draft.provider).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          {/* Temperature */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Temperature: {draft.temperature}
            </label>
            <input
              type="range"
              min="0" max="1" step="0.1"
              value={draft.temperature}
              onChange={(e) => setDraft(d => ({ ...d, temperature: parseFloat(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
              {TEMP_PRESETS.map(p => (
                <button
                  key={p.label}
                  onClick={() => setDraft(d => ({ ...d, temperature: p.value }))}
                  className={`hover:text-foreground ${draft.temperature === p.value ? 'text-foreground font-medium' : ''}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {/* Max Tokens */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Max Tokens</label>
            <input
              type="number"
              value={draft.max_tokens}
              onChange={(e) => setDraft(d => ({ ...d, max_tokens: parseInt(e.target.value) || 1024 }))}
              className="w-full text-xs px-2 py-1.5 rounded-md border bg-background"
              min="256" max="16384" step="256"
            />
          </div>
        </div>

        {/* Cost estimate + capability badges */}
        {(() => {
          const reg = MODEL_REGISTRY[draft.model];
          if (!reg) return null;
          const est = service?.processing?.estimated_tokens;
          const costPerCall = est
            ? (est.input / 1_000_000) * reg.cost.input + (est.output / 1_000_000) * reg.cost.output
            : null;

          return (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {/* Cost */}
              <span className="text-xs text-emerald-500">
                ${reg.cost.input}/{reg.cost.output} per M tokens
                {costPerCall != null && <span className="ml-1 font-medium">(~${costPerCall.toFixed(5)}/call)</span>}
              </span>
              {/* Context window */}
              <span className="text-[10px] text-muted-foreground">
                {(reg.context_window / 1000).toFixed(0)}K ctx
              </span>
              {/* Capabilities */}
              {reg.capabilities.map(cap => (
                <Badge key={cap} variant="outline" className="text-[10px] font-normal">
                  {cap}
                </Badge>
              ))}
              {/* Vision warning */}
              {service.type === 'extract' && !reg.capabilities.includes('vision') && (
                <span className="text-[10px] text-amber-500">This model does not support vision</span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Prompts */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Role</h4>
        <textarea
          value={draft.role}
          onChange={(e) => setDraft(d => ({ ...d, role: e.target.value }))}
          className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y min-h-[80px]"
          rows={4}
        />
      </div>

      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Task</h4>
        <textarea
          value={draft.task}
          onChange={(e) => setDraft(d => ({ ...d, task: e.target.value }))}
          className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y min-h-[80px]"
          rows={4}
        />
      </div>

      {/* Guidelines Toggles */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Guidelines</h4>
        <div className="space-y-3">
          {TOGGLE_DEFS.map((td) => (
            <div key={td.key} className="space-y-1">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-foreground">{td.label}</label>
                <span className="text-[10px] text-muted-foreground">{td.help}</span>
              </div>
              <div className="flex gap-1">
                {td.options.map((opt) => {
                  const isSelected = draft.guidelines_toggles[td.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDraft(d => ({
                        ...d,
                        guidelines_toggles: { ...d.guidelines_toggles, [td.key]: opt.value },
                      }))}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30'
                      }`}
                      title={opt.desc}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Guidelines */}
        <div className="mt-3">
          <label className="text-xs font-medium text-foreground block mb-1">Custom Additions</label>
          <textarea
            value={draft.guidelines_custom}
            onChange={(e) => setDraft(d => ({ ...d, guidelines_custom: e.target.value }))}
            placeholder="Additional guidelines appended after toggle-generated text..."
            className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y"
            rows={2}
          />
        </div>

        {/* Generated Preview */}
        <div className="mt-3">
          <label className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1">Preview (auto-generated)</label>
          <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-32 whitespace-pre-wrap font-mono text-muted-foreground">
            {previewGuidelinesText(draft.guidelines_toggles, draft.guidelines_custom) || '(no guidelines configured)'}
          </pre>
        </div>
      </div>

      {/* Permissions */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Permissions</h4>
        <div className="space-y-2.5">
          {/* Can Write Graph */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.permissions.can_write_graph ?? true}
              onChange={(e) => setDraft(d => ({
                ...d,
                permissions: { ...d.permissions, can_write_graph: e.target.checked },
              }))}
              className="rounded border-muted-foreground/40"
            />
            <span className="text-xs text-foreground">Can write to graph</span>
            <span className="text-[10px] text-muted-foreground">— service output creates/updates nodes and edges</span>
          </label>

          {/* Can Modify Existing */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.permissions.can_modify_existing ?? false}
              onChange={(e) => setDraft(d => ({
                ...d,
                permissions: { ...d.permissions, can_modify_existing: e.target.checked },
              }))}
              className="rounded border-muted-foreground/40"
            />
            <span className="text-xs text-foreground">Can modify existing data</span>
            <span className="text-[10px] text-muted-foreground">— update vs create-only</span>
          </label>

          {/* Can Trigger Services */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.permissions.can_trigger_services ?? false}
              onChange={(e) => setDraft(d => ({
                ...d,
                permissions: { ...d.permissions, can_trigger_services: e.target.checked },
              }))}
              className="rounded border-muted-foreground/40"
            />
            <span className="text-xs text-foreground">Can trigger other services</span>
            <span className="text-[10px] text-muted-foreground">— dispatch follow-up AI services</span>
          </label>

          {/* Allowed Node Types (from schema_contribution) */}
          {service.schema_contribution?.node_types?.length > 0 && (draft.permissions.can_write_graph ?? true) && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground block mb-1">Allowed Node Types</label>
              <div className="flex flex-wrap gap-1.5">
                {service.schema_contribution.node_types.map((nt) => {
                  const allowed = draft.permissions.allowed_node_types || service.schema_contribution.node_types.map(n => n.label);
                  const isChecked = allowed.includes(nt.label);
                  return (
                    <label key={nt.label} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const current = draft.permissions.allowed_node_types || service.schema_contribution.node_types.map(n => n.label);
                          const next = e.target.checked
                            ? [...new Set([...current, nt.label])]
                            : current.filter(t => t !== nt.label);
                          setDraft(d => ({
                            ...d,
                            permissions: { ...d.permissions, allowed_node_types: next },
                          }));
                        }}
                        className="rounded border-muted-foreground/40"
                      />
                      <span className="text-xs">{nt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Allowed Edge Types (from schema_contribution) */}
          {service.schema_contribution?.edge_types?.length > 0 && (draft.permissions.can_write_graph ?? true) && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground block mb-1">Allowed Edge Types</label>
              <div className="flex flex-wrap gap-1.5">
                {service.schema_contribution.edge_types.map((et) => {
                  const allowed = draft.permissions.allowed_edge_types || service.schema_contribution.edge_types.map(e => e.type);
                  const isChecked = allowed.includes(et.type);
                  return (
                    <label key={et.type} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const current = draft.permissions.allowed_edge_types || service.schema_contribution.edge_types.map(e => e.type);
                          const next = e.target.checked
                            ? [...new Set([...current, et.type])]
                            : current.filter(t => t !== et.type);
                          setDraft(d => ({
                            ...d,
                            permissions: { ...d.permissions, allowed_edge_types: next },
                          }));
                        }}
                        className="rounded border-muted-foreground/40"
                      />
                      <span className="text-xs">{et.type}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Framework Dimensions (read-only display) */}
      {service.framework_definition?.dimensions && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Framework ({service.framework_definition.dimensions.length} dimensions)
          </h4>
          <div className="flex flex-wrap gap-1">
            {service.framework_definition.dimensions.map((d) => (
              <Badge key={d.id} variant="outline" className="text-xs font-normal">
                {d.name || d.id}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Schema Contribution (read-only display) */}
      {service.schema_contribution && (service.schema_contribution.node_types?.length > 0 || service.schema_contribution.edge_types?.length > 0) && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schema Contribution</h4>
          <div className="text-xs space-y-1">
            {service.schema_contribution.node_types?.map((n) => (
              <div key={n.label} className="text-muted-foreground">
                Node: <span className="text-foreground font-medium">{n.label}</span>
                <span className="text-muted-foreground/60"> ({(n.properties || []).join(', ')})</span>
              </div>
            ))}
            {service.schema_contribution.edge_types?.map((e, i) => (
              <div key={i} className="text-muted-foreground">
                Edge: <span className="text-foreground font-medium">{e.from}</span>
                {' '}-{e.type}-{'>'} <span className="text-foreground font-medium">{e.to}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Test Section */}
      <div className="border-t pt-4">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Test Service</h4>
        <textarea
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          placeholder="Paste sample content to test this service..."
          className="w-full text-xs font-mono px-3 py-2 rounded-md border bg-background resize-y"
          rows={3}
        />
        <button
          onClick={handleTest}
          disabled={testing || !testText.trim()}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
        >
          {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Test
        </button>
        {testResult && (
          <div className="mt-2">
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-60 whitespace-pre-wrap font-mono">
              {JSON.stringify(testResult.parsed, null, 2)}
            </pre>
            {testResult.usage && (
              <div className="text-[10px] text-muted-foreground mt-1">
                Tokens: {testResult.usage.input_tokens} in / {testResult.usage.output_tokens} out
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 border-t pt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save Changes
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-md border hover:bg-muted transition-colors"
        >
          <X className="h-3 w-3" /> Cancel
        </button>
      </div>
    </div>
  );
}

// ==========================================
// SERVICE CARD
// ==========================================

function ServiceCard({ service, isExpanded, isEditing, onToggle, onEdit, onEditDone }) {
  const Icon = SERVICE_ICONS[service.serviceId] || Brain;
  const hasLLM = !!service.llm_config;
  const dimCount = service.framework_definition?.dimensions?.length;

  return (
    <Card className={`transition-colors ${isEditing ? 'border-primary/30' : 'cursor-pointer hover:border-foreground/20'}`}>
      <CardHeader className="pb-3" onClick={isEditing ? undefined : onToggle} style={{ cursor: isEditing ? 'default' : 'pointer' }}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-muted">
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-sm font-medium">{service.name}</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {service.serviceId}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">v{service.version}</Badge>
            {hasLLM ? (
              <Badge variant="secondary" className="text-xs">{MODEL_REGISTRY[service.llm_config?.model]?.name || service.llm_config?.model?.split('-').slice(0, 2).join('-')}</Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-muted-foreground">No LLM</Badge>
            )}
            {hasLLM && !isEditing && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-1 rounded hover:bg-muted transition-colors"
                title="Edit service"
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
            {!isEditing && (isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground">{service.description}</p>

        {/* Quick stats row */}
        <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
          {dimCount && <span>{dimCount} dimensions</span>}
          {service.processing?.estimated_tokens && (
            <span>~{service.processing.estimated_tokens.input + service.processing.estimated_tokens.output} tokens</span>
          )}
          {service.processing?.timeout_ms && (
            <span>{(service.processing.timeout_ms / 1000).toFixed(0)}s timeout</span>
          )}
          {(() => {
            const cost = estimateCost(service);
            return cost ? <span className="text-emerald-500">${cost.toFixed(5)}/call</span> : null;
          })()}
        </div>

        {/* Edit mode */}
        {isEditing && (
          <ServiceEditForm
            service={service}
            onSave={onEditDone}
            onCancel={onEditDone}
          />
        )}

        {/* Read-only expanded detail */}
        {isExpanded && !isEditing && (
          <div className="mt-4 space-y-4 border-t pt-4">
            {/* LLM Config */}
            {hasLLM && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Model Config</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Provider:</span> {PROVIDERS.find(p => p.id === service.llm_config?.provider)?.label || service.llm_config?.provider}</div>
                  <div><span className="text-muted-foreground">Model:</span> {MODEL_REGISTRY[service.llm_config?.model]?.name || service.llm_config?.model}</div>
                  <div><span className="text-muted-foreground">Temperature:</span> {service.llm_config?.temperature}</div>
                  <div><span className="text-muted-foreground">Max tokens:</span> {service.llm_config?.max_tokens}</div>
                </div>
                {MODEL_REGISTRY[service.llm_config?.model]?.capabilities && (
                  <div className="flex gap-1 mt-2">
                    {MODEL_REGISTRY[service.llm_config?.model].capabilities.map(cap => (
                      <Badge key={cap} variant="outline" className="text-[10px] font-normal">{cap}</Badge>
                    ))}
                    <span className="text-[10px] text-muted-foreground ml-1">
                      {(MODEL_REGISTRY[service.llm_config?.model].context_window / 1000).toFixed(0)}K ctx
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Framework Definition */}
            {service.framework_definition?.dimensions && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Framework ({service.framework_definition.dimensions.length} dimensions)
                </h4>
                <div className="flex flex-wrap gap-1">
                  {service.framework_definition.dimensions.map((d) => (
                    <Badge key={d.id} variant="outline" className="text-xs font-normal">
                      {d.name || d.id}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Schema Contribution */}
            {service.schema_contribution && (service.schema_contribution.node_types?.length > 0 || service.schema_contribution.edge_types?.length > 0) && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schema Contribution</h4>
                <div className="text-xs space-y-1">
                  {service.schema_contribution.node_types?.map((n) => (
                    <div key={n.label} className="text-muted-foreground">
                      Node: <span className="text-foreground font-medium">{n.label}</span>
                    </div>
                  ))}
                  {service.schema_contribution.edge_types?.map((e, i) => (
                    <div key={i} className="text-muted-foreground">
                      Edge: <span className="text-foreground font-medium">{e.from}</span> -{e.type}-{'>'} <span className="text-foreground font-medium">{e.to}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prompt Preview */}
            {service.prompt_config?.role && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Prompt (Role)</h4>
                <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-40 whitespace-pre-wrap font-mono">
                  {service.prompt_config.role}
                </pre>
              </div>
            )}

            {/* Guidelines Toggles Summary */}
            {service.prompt_config?.guidelines_toggles && Object.keys(service.prompt_config.guidelines_toggles).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Guidelines</h4>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {TOGGLE_DEFS.map((td) => {
                    const val = service.prompt_config.guidelines_toggles[td.key];
                    if (!val) return null;
                    const opt = td.options.find(o => o.value === val);
                    return (
                      <Badge key={td.key} variant="outline" className="text-xs font-normal">
                        {td.label}: {opt?.label || val}
                      </Badge>
                    );
                  })}
                </div>
                {service.prompt_config.guidelines_text && (
                  <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-32 whitespace-pre-wrap font-mono text-muted-foreground">
                    {service.prompt_config.guidelines_text}
                  </pre>
                )}
              </div>
            )}

            {/* Permissions Summary */}
            {service.permissions && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Permissions</h4>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant={service.permissions.can_write_graph !== false ? 'default' : 'destructive'} className="text-xs font-normal">
                    {service.permissions.can_write_graph !== false ? 'Can write graph' : 'No graph writes'}
                  </Badge>
                  {service.permissions.can_modify_existing && (
                    <Badge variant="outline" className="text-xs font-normal">Can modify existing</Badge>
                  )}
                  {service.permissions.can_trigger_services && (
                    <Badge variant="outline" className="text-xs font-normal">Can trigger services</Badge>
                  )}
                  {service.permissions.write_mode && (
                    <Badge variant="outline" className="text-xs font-normal">Mode: {service.permissions.write_mode}</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// MAIN PAGE
// ==========================================

export default function ServiceCatalogPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const [reseeding, setReseeding] = useState(false);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceCatalogApi.list();
      const data = res.data?.data;
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load service catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleReseed = async () => {
    setReseeding(true);
    setError(null);
    try {
      await serviceCatalogApi.reseed();
      await loadServices();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Reseed failed');
    } finally {
      setReseeding(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleEditDone = () => {
    setEditingId(null);
    loadServices(); // Refresh to show saved changes
  };

  // Group services by category
  const grouped = {};
  for (const svc of services) {
    const cat = svc.category || 'system';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(svc);
  }

  const categoryOrder = ['classify', 'extract', 'enrich', 'system'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Service Catalog</h1>
          <p className="text-muted-foreground">
            Platform service definitions. {services.length} services registered.
            {services.length > 0 && (() => {
              const totalCost = services.reduce((sum, s) => sum + (estimateCost(s) || 0), 0);
              return totalCost > 0 ? (
                <span className="ml-2 text-emerald-500">${totalCost.toFixed(4)}/ingest</span>
              ) : null;
            })()}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCreating(true)}
            disabled={creating}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            Create Service
          </button>
          <button
            onClick={handleReseed}
            disabled={reseeding || loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
          >
            {reseeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Reseed
          </button>
          <button
            onClick={loadServices}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Create Service Form */}
      {creating && (
        <ServiceCreateForm
          onCreated={() => { setCreating(false); loadServices(); }}
          onCancel={() => setCreating(false)}
        />
      )}

      {/* Loading */}
      {loading && services.length === 0 && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading service catalog...
        </div>
      )}

      {/* Service categories */}
      {categoryOrder.map((cat) => {
        const meta = CATEGORY_META[cat];
        const catServices = grouped[cat];
        if (!catServices || catServices.length === 0) return null;

        return (
          <div key={cat} className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className={meta.color}>{meta.label}</Badge>
              <span className="text-xs text-muted-foreground">{meta.description}</span>
              <span className="text-xs text-muted-foreground ml-auto">{catServices.length} services</span>
            </div>
            <div className="grid gap-3">
              {catServices.map((svc) => (
                <ServiceCard
                  key={svc.serviceId}
                  service={svc}
                  isExpanded={expandedId === svc.serviceId}
                  isEditing={editingId === svc.serviceId}
                  onToggle={() => {
                    if (editingId !== svc.serviceId) {
                      setExpandedId(expandedId === svc.serviceId ? null : svc.serviceId);
                    }
                  }}
                  onEdit={() => {
                    setEditingId(svc.serviceId);
                    setExpandedId(null);
                  }}
                  onEditDone={handleEditDone}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
